import { useState, useEffect, useMemo, useCallback } from 'react'
import { useDebounce } from 'use-debounce'
import { Modal } from 'components/UI/Modal/Modal'
import { TextInput } from 'components/FormUtilsV2/TextInput/text-input'
import { useForm } from 'react-hook-form'
import FbButton from 'components/FbUI/FbButton'
import { CircleCheck, ExternalLink, X } from 'lucide-react'
import { cn } from 'components/UI/cn'
import { getChainPageUrl } from 'utils/oppTypesMap'
import useApiService from 'services/api'
import { toast } from 'react-toastify'
import { useNavigate } from 'react-router-dom'
import dialogConfirm from 'components/dialogConfirm'
import { Tabs } from 'components/FbUI/Tabs'
import { ContactCompanySearchResult } from 'models/companies'

interface SingleChainMergeModalProps {
  contactCompanyId: number
  contactCompanyName: string
  show: boolean
  onClose: () => void
}

export function SingleChainMergeModal({
  contactCompanyId,
  contactCompanyName,
  show,
  onClose,
}: SingleChainMergeModalProps) {
  const navigate = useNavigate()
  const api = useApiService()

  // Modal state is now controlled externally via props
  const [selectedTab, setSelectedTab] = useState<'firstbite' | 'custom'>(
    'firstbite'
  )

  // Unified search state
  const [search, setSearch] = useState('')
  const [debouncedSearch] = useDebounce(search, 500)

  // Selected items
  const [selectedFbChain, setSelectedFbChain] =
    useState<ContactCompanySearchResult | null>(null)
  const [selectedCustomCompany, setSelectedCustomCompany] =
    useState<ContactCompanySearchResult | null>(null)

  // Search results state
  const [fbSearchResults, setFbSearchResults] = useState<any>(null)
  const [isFbLoading, setIsFbLoading] = useState(false)
  const [customSearchResults, setCustomSearchResults] = useState<any>(null)
  const [isCustomLoading, setIsCustomLoading] = useState(false)

  const { control } = useForm({
    defaultValues: {
      search: '',
    },
  })

  // Perform search for both FB and custom companies simultaneously
  const performSearch = useCallback(
    async (searchValue: string) => {
      if (!searchValue.trim()) {
        setFbSearchResults(null)
        setCustomSearchResults(null)
        return
      }

      // Set loading states
      setIsFbLoading(true)
      setIsCustomLoading(true)

      // Prepare search options
      const searchOptions = {
        category_filter: 'all',
        type_filter: 'all',
        limit: 20,
        offset: 0,
        sort_by: 'relevance',
      }

      // Make both API calls in parallel
      const promises = [
        api
          .searchFirstBiteCompanies(searchValue, searchOptions)
          .then((data) => {
            setFbSearchResults(data)
            setIsFbLoading(false)
          })
          .catch((error) => {
            console.error('FB Search error:', error)
            setFbSearchResults(null)
            setIsFbLoading(false)
          }),

        api
          .searchUnmatchedCompanies(searchValue, searchOptions)
          .then((data) => {
            setCustomSearchResults(data)
            setIsCustomLoading(false)
          })
          .catch((error) => {
            console.error('Custom Search error:', error)
            setCustomSearchResults(null)
            setIsCustomLoading(false)
          }),
      ]

      // Wait for both to complete (but don't block on individual failures)
      await Promise.allSettled(promises)
    },
    [api]
  )

  // Effect to handle search when search term changes
  useEffect(() => {
    performSearch(debouncedSearch)
  }, [debouncedSearch, performSearch])

  // Handle tab change
  const handleTabChange = useCallback((tab: string) => {
    setSelectedTab(tab as 'firstbite' | 'custom')
    // Reset selections when switching tabs
    setSelectedFbChain(null)
    setSelectedCustomCompany(null)
  }, [])

  // Handle merge
  const handleMerge = useCallback(async () => {
    const isFbMerge = selectedTab === 'firstbite'
    const selectedCompany = isFbMerge ? selectedFbChain : selectedCustomCompany

    // Validate selection
    const destinationId = isFbMerge
      ? selectedFbChain?.contact_company_id || selectedFbChain?.item_id
      : selectedCustomCompany?.contact_company_id

    if (!contactCompanyId || !selectedCompany || !destinationId) {
      toast.error('Please select a valid destination company')
      return
    }

    // Confirm merge
    const mergeType = isFbMerge ? 'First Bite Company' : 'Custom Company'
    const confirmed = await dialogConfirm(
      `Are you sure you want to merge ${contactCompanyName} to ${selectedCompany.name}? This action cannot be undone.`,
      `Merge Company with ${mergeType}`
    )

    if (!confirmed) {
      return
    }

    try {
      // Perform the merge
      const destId =
        typeof destinationId === 'string'
          ? parseInt(destinationId, 10)
          : destinationId

      if (isFbMerge && selectedFbChain) {
        await api.mergeCompanies(
          contactCompanyId,
          destId,
          selectedFbChain.type || 'RE'
        )

        // Navigate to the FB company
        navigate(
          getChainPageUrl(selectedFbChain.type || 'RE', String(destinationId))
        )
      } else if (!isFbMerge && selectedCustomCompany) {
        await api.mergeCustomCompanies(contactCompanyId, destId)

        // Navigate to the custom company with full page reload
        window.location.href = `/company/UG${destId}`
      }

      toast.success('Companies merged successfully')
    } catch (error: any) {
      console.error(error)
      toast.error('Failed to merge companies')
    }
  }, [
    selectedTab,
    contactCompanyId,
    contactCompanyName,
    selectedFbChain,
    selectedCustomCompany,
    api,
    navigate,
  ])

  const canProceed = useMemo(() => {
    return selectedTab === 'firstbite'
      ? selectedFbChain !== null &&
          (selectedFbChain.contact_company_id !== undefined ||
            selectedFbChain.item_id !== undefined)
      : selectedCustomCompany !== null &&
          selectedCustomCompany.contact_company_id !== undefined
  }, [selectedTab, selectedFbChain, selectedCustomCompany])

  // Get current search results based on tab
  const currentResults =
    selectedTab === 'firstbite'
      ? fbSearchResults?.results || []
      : customSearchResults?.results || []

  const isLoading = selectedTab === 'firstbite' ? isFbLoading : isCustomLoading

  return (
    <Modal
      open={show}
      onOpenChange={(open) => !open && onClose()}
      title="Merge Company"
      description="Merge this company with another company. All doors, deals, contacts, notes, tags, and known distributors will be transferred to the destination company, and this page will be deleted."
      size="lg"
      onCancel={onClose}
      onAccept={handleMerge}
      blockAccept={!canProceed}
      acceptButtonText="Continue"
      cancelButtonText="Cancel"
    >
      <div className="flex flex-col gap-4">
        {/* Search Input - Above tabs like UnifiedSearch */}
        <TextInput
          name="search"
          control={control}
          placeholder="Search for a company to merge with"
          onChange={(value) => setSearch(value)}
        />

        {/* Tabs */}
        <Tabs
          tabs={[
            {
              key: 'firstbite',
              title: 'FB Companies',
            },
            {
              key: 'custom',
              title: 'Custom Companies',
            },
          ]}
          selectedTab={selectedTab}
          setSelectedTab={handleTabChange}
        />

        {/* Results */}
        <div className="h-400px">
          {isLoading ? (
            <div className="flex items-center justify-center p-4 h-[416px]">
              Loading...
            </div>
          ) : !debouncedSearch ? (
            <div className="flex items-center justify-center p-4 text-gray-500">
              Start typing to search for companies
            </div>
          ) : currentResults.length === 0 ? (
            <div className="flex items-center justify-center p-4">
              No results found
            </div>
          ) : (
            <div className="flex flex-col gap-2 flex-1 border px-2 rounded">
              <div className="flex flex-col gap-2 max-h-[400px] overflow-y-auto">
                {selectedTab === 'firstbite'
                  ? // FB Companies Results
                    fbSearchResults?.results?.map(
                      (company: ContactCompanySearchResult, index: number) => (
                        <FbChainSearchResultItem
                          key={
                            company.contact_company_id ||
                            company.item_id ||
                            index
                          }
                          company={company}
                          index={index}
                          searchResults={fbSearchResults.results}
                          onSelect={setSelectedFbChain}
                        />
                      )
                    )
                  : // Custom Companies Results
                    customSearchResults?.results
                      ?.filter(
                        (company: ContactCompanySearchResult) =>
                          company.contact_company_id !== contactCompanyId
                      )
                      .map(
                        (
                          company: ContactCompanySearchResult,
                          index: number
                        ) => (
                          <CustomCompanySearchResultItem
                            key={
                              company.contact_company_id ||
                              company.item_id ||
                              index
                            }
                            company={company}
                            index={index}
                            searchResults={customSearchResults.results}
                            onSelect={setSelectedCustomCompany}
                          />
                        )
                      )}
              </div>
            </div>
          )}
        </div>

        {/* Selected Item Display */}
        {selectedFbChain && selectedTab === 'firstbite' && (
          <div className="mt-2 p-2 border-2 border-fb-dark-green rounded bg-gray-50">
            <div className="text-sm font-semibold mb-1">Selected Company:</div>
            <FbChainSearchResultItem
              company={selectedFbChain}
              index={0}
              searchResults={fbSearchResults?.results || []}
              onSelect={setSelectedFbChain}
              isSelected
            />
          </div>
        )}

        {selectedCustomCompany && selectedTab === 'custom' && (
          <div className="mt-2 p-2 border-2 border-fb-dark-green rounded bg-gray-50">
            <div className="text-sm font-semibold mb-1">Selected Company:</div>
            <CustomCompanySearchResultItem
              company={selectedCustomCompany}
              index={0}
              searchResults={customSearchResults?.results || []}
              onSelect={setSelectedCustomCompany}
              isSelected
            />
          </div>
        )}
      </div>
    </Modal>
  )
}

function FbChainSearchResultItem({
  company,
  index,
  searchResults,
  onSelect,
  isSelected = false,
}: {
  company: ContactCompanySearchResult
  index: number
  searchResults: ContactCompanySearchResult[]
  onSelect: (company: ContactCompanySearchResult | null) => void
  isSelected?: boolean
}) {
  const handleSelect = () => {
    if (!isSelected) {
      onSelect(company)
    }
  }

  return (
    <div
      className={cn(
        'flex items-center justify-between p-2 border rounded cursor-pointer hover:bg-gray-50',
        index === 0 && 'mt-2',
        index === searchResults.length - 1 && 'mb-2',
        isSelected && 'bg-fb-dark-green text-white hover:bg-fb-dark-green'
      )}
      onClick={handleSelect}
    >
      <div className="flex flex-col gap-1">
        <div className="font-bold">
          {company.name} {company.type && `(${company.type})`}
        </div>
        <div className="text-xs">{company.domain}</div>
        {company.size && (
          <div className="text-xs">{company.size} locations</div>
        )}
        {company.city && company.state && (
          <div className="text-xs">
            {company.city}, {company.state}
          </div>
        )}
      </div>
      <div className="flex flex-row gap-2">
        <FbButton
          type="button"
          variant="secondary"
          size="sm"
          onClick={(e) => {
            e.stopPropagation()
            window.open(
              getChainPageUrl(
                company.type || 'RE',
                String(company.contact_company_id)
              ),
              '_blank'
            )
          }}
        >
          <ExternalLink size={16} />
          View
        </FbButton>
        {!isSelected ? (
          <FbButton
            type="button"
            variant="primary"
            size="sm"
            onClick={(e) => {
              e.stopPropagation()
              onSelect(company)
            }}
          >
            <CircleCheck size={16} />
            Select
          </FbButton>
        ) : (
          <FbButton
            variant="primary"
            size="sm"
            onClick={(e) => {
              e.stopPropagation()
              onSelect(null)
            }}
          >
            <X size={16} />
          </FbButton>
        )}
      </div>
    </div>
  )
}

function CustomCompanySearchResultItem({
  company,
  index,
  searchResults,
  onSelect,
  isSelected = false,
}: {
  company: ContactCompanySearchResult
  index: number
  searchResults: ContactCompanySearchResult[]
  onSelect: (company: ContactCompanySearchResult | null) => void
  isSelected?: boolean
}) {
  return (
    <div
      className={cn(
        'flex items-center justify-between p-2 border rounded cursor-pointer hover:bg-gray-50',
        index === 0 && 'mt-2',
        index === searchResults.length - 1 && 'mb-2',
        isSelected && 'bg-fb-dark-green text-white hover:bg-fb-dark-green'
      )}
      onClick={() => !isSelected && onSelect(company)}
    >
      <div className="flex flex-col gap-1">
        <div className="font-bold">{company.name || '(No name)'}</div>
        <div className="text-xs">{company.domain || '(No domain)'}</div>
        <div className="text-xs">
          {company.city && company.state
            ? `${company.city}, ${company.state}`
            : '(No location)'}
        </div>
        {company.size && <div className="text-xs">Size: {company.size}</div>}
      </div>
      <div className="flex flex-row gap-2">
        <FbButton
          type="button"
          variant="secondary"
          size="sm"
          onClick={(e) => {
            e.stopPropagation()
            window.open(`/company/${company.contact_company_id}`, '_blank')
          }}
        >
          <ExternalLink size={16} />
          View
        </FbButton>
        {!isSelected ? (
          <FbButton
            type="button"
            variant="primary"
            size="sm"
            onClick={(e) => {
              e.stopPropagation()
              onSelect(company)
            }}
          >
            <CircleCheck size={16} />
            Select
          </FbButton>
        ) : (
          <FbButton
            variant="primary"
            size="sm"
            onClick={(e) => {
              e.stopPropagation()
              onSelect(null)
            }}
          >
            <X size={16} />
          </FbButton>
        )}
      </div>
    </div>
  )
}
